import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Link } from 'react-router-dom'
import { IonButton, IonPopover } from '@ionic/react'
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import { Warning } from './Roster'
import { calculatePositions } from './util'
import {
  useEditMode,
  useHaveValidSeason,
  useMyBest,
  useMyFieldPositions,
  useMyGameFormat,
  useMyGameId,
	useMyLocksState,
  useMyNumPeriods,
	useMyPlayerNames,
  useSetAmDragging,
  useSwapPlayers,
	useSwapEndPositions,
  useSwapPositions,
  useSwapQtrs,
  useToggleLock,
} from './data'
import GridCell from './GridCell'
import GridPlayers from './GridPlayers'
import GridPositions from './GridPositions'
import GridPreferences from './GridPreferences'
import LoadingSpinner from './LoadingSpinner'
import {queryFieldPositions} from './definitions'
import { normalizeEndRoster } from './rosterAnalysis'
import SubstitutionDragOverlay from './SubstitutionDragOverlay'
import { useSubstitutionDrag } from './useSubstitutionDrag'
import {
	canUseLegacySubstitutionGesture,
	recordLongPressSubstitutionActivation,
	shouldShowSubstitutionGestureHelp,
} from './substitutionGestureHelp'
import {MoreInfoLink} from './MoreInfoLink'

// The transitional helper can be fully removed after December 16, 2026.
const LEGACY_SUB_ACTIVATION_MS = 1600

type DisplayRosterProps = {
	data: AllRosterStats | undefined | null
	loading: boolean
	showAltBoard: boolean
	showPositionPreferences: boolean
	toggleAvailable: (player: PlayerId) => void
	setOrangeman: (player: PlayerId) => void
	onSwapPlayers: () => void
	numPlayersShown: number
	showAllPlayers: boolean
	setShowAllPlayers: (showAll: boolean) => void
	onCourtPlayers: PlayerId[]
}

const DisplayRoster = (props: DisplayRosterProps) => {

	const { data, loading, showAltBoard, showPositionPreferences, toggleAvailable, setOrangeman, onSwapPlayers } = props

    const [ _, forceUpdate ] = useReducer(x => x + 1, 0)

    const editMode = useEditMode()

    const best = useMyBest()
    const gameId = useMyGameId() || ''
	const numPeriods = useMyNumPeriods()
	const gameFormat = useMyGameFormat()
	const playerNames = useMyPlayerNames()
	const [ locks, setLocks ] = useMyLocksState()
	const [ legacySubHelpIndex, setLegacySubHelpIndex ] = useState<number | null>(null)
	const [ showLegacySubHelp, setShowLegacySubHelp ] = useState(false)
	const legacySubHelpTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const legacySubHelpNextTapIndex = useRef<RosterPositionIndex | null>(null)
	const legacySubHelpPopover = useRef<HTMLIonPopoverElement>(null)
	const bestRef = useRef(best)
	const locksRef = useRef(locks)
	bestRef.current = best
	locksRef.current = locks

	const setAmDragging = useSetAmDragging()

    const swapPositions = useSwapPositions(gameId)
	const swapEndPositions = useSwapEndPositions(gameId)
    const swapPlayers = useSwapPlayers(gameId)
	const swapQtrs = useSwapQtrs(gameId)
	const toggleLock = useToggleLock(gameId)

	const haveValidSeason = useHaveValidSeason()

	const fieldPositions = useMyFieldPositions()
	const cancelLegacySubHelp = useCallback(() => {
		if (legacySubHelpTimer.current) {
			clearTimeout(legacySubHelpTimer.current)
			legacySubHelpTimer.current = null
		}
		legacySubHelpNextTapIndex.current = null
		setLegacySubHelpIndex(null)
	}, [ ])

	useEffect(() => () => {
		if (legacySubHelpTimer.current) {
			clearTimeout(legacySubHelpTimer.current)
		}
	}, [ ])

	const startLegacySubHelp = useCallback((index: RosterPositionIndex) => {
		if (legacySubHelpNextTapIndex.current === index) {
			return false
		}

		if (
			!locksRef.current[index] ||
			!shouldShowSubstitutionGestureHelp() ||
			!canUseLegacySubstitutionGesture(index, bestRef.current, numPeriods, fieldPositions.length)
		) {
			return false
		}

		cancelLegacySubHelp()
		legacySubHelpNextTapIndex.current = index
		setLegacySubHelpIndex(index)
		legacySubHelpTimer.current = setTimeout(() => {
			legacySubHelpTimer.current = null
			legacySubHelpNextTapIndex.current = null
			setLocks(currentLocks => {
				const nextLocks = currentLocks.slice()
				nextLocks[index] = false
				return nextLocks
			})
			const tile = document.querySelector<HTMLElement>(`.ion-page:not(.ion-page-hidden) .cell-position[data-i="${index}"]`)
			if (tile && legacySubHelpPopover.current) {
				const bounds = tile.getBoundingClientRect()
				legacySubHelpPopover.current.event = new MouseEvent('click', {
					clientX: bounds.left + bounds.width / 2,
					clientY: bounds.top + bounds.height / 2,
				})
			}
			setLegacySubHelpIndex(null)
			setShowLegacySubHelp(true)
		}, LEGACY_SUB_ACTIVATION_MS)
		return true
	}, [ cancelLegacySubHelp, fieldPositions.length, numPeriods, setLocks ])

	const onLongPressActivated = useCallback(() => {
		cancelLegacySubHelp()
	}, [ cancelLegacySubHelp ])
	const onLongPressChanged = useCallback(() => {
		recordLongPressSubstitutionActivation()
	}, [ ])

	const endRoster = normalizeEndRoster(best.roster, best.endRoster, best.subs, numPeriods, gameFormat)
	const substitutionDrag = useSubstitutionDrag({
		roster: best.roster,
		endRoster,
		editMode,
		showAltBoard,
		numPeriods,
		fieldPositions,
		playerNames,
		setAmDragging,
		swapEndPositions,
		onLongPressActivated,
		onLongPressChanged,
	})

	// console.log("DisplayRoster.js render", fieldPositions)

    if (loading) {
        return (
            <LoadingSpinner />
        )
    }

	if (!haveValidSeason) {
		return (
			<Warning>
				This game belongs to a deleted season and cannot be modified.
			</Warning>

		)
	}
	if (!best.players.length) {
		return (
			<Warning>
				No players have been added yet.
				{' '}
				<Link
					to={{
						pathname: "/players",
					}}

				>
					Add some players.
				</Link>
			</Warning>
		)
	}

    //
    // *** IMPORTANT ***
    // So this motherfucker is bound at mount-time and can't
	// rely on reading any up-to-date values! That's super-annoying.
	// I need to be very careful that I don't rely on any values
	// that might change after mount.
	//
	// You *can* use dynamic variables in a callback function that has been
	// supplied to onDrop via props.
	//
	function onDrop(_event: React.DragEvent, obj: OnDropObject) {

		substitutionDrag.onNormalDragEnd()
		setAmDragging(false)
		if (substitutionDrag.isNormalDropSuppressed()) {
			return
		}

		const selector = ('qtr' in obj) ? `[data-qtr="${obj.qtr}"]` : `[data-i="${'i' in obj ? obj.i : obj.player}"]`

		const { xCenter, yCenter } = getBoundingBox(selector)

		console.log('onDrop!', obj, selector, getBoundingBox(selector), xCenter, yCenter)

		let droppedOnPosition = false

		const isQtrHeader = 'qtr' in obj

		const madeChanges =
			xCenter !== undefined &&
			yCenter !== undefined &&
			document.elementsFromPoint.apply(document, [ xCenter, yCenter ]).some(element => {

			// console.log('onDrop checking element', element)

			//
			// Figure out whether this element is being dropped in a position space,
			// because if so, later we might use this info to decide to toggle its lock.
			//
			// Unfortunately it also counts when a position is dropped in the cell-header,
			// so we have to manually reverse that further below.
			//
			if (element.classList.contains('column-positions-without-header')) {
				droppedOnPosition = true
			}

			if (isQtrHeader) {
				// console.log('isQtrHeader checking ', element)
				if (element.classList.contains('cell-header')) {
					// console.log('One header dropped on another header')
					const qtr = Number(element.getAttribute('data-qtr'))
					if (qtr === obj.qtr) {
						// The header was dropped back where it started
						return false
					}
					// console.log('Qtr header dropped successfully?', qtr, '<=>', obj.qtr)
					Haptics.impact({ style: ImpactStyle.Light })
					swapQtrs(qtr, obj.qtr)
					return true
				}
			} else if (element.classList.contains('posed-element')) {

				//
				// We dropped something on the grid.
				//
				const tile = element.firstChild
				if (tile && tile instanceof HTMLElement && !tile.classList.contains('cell-header')) {
					const i = tile.getAttribute('data-i')
					console.log(obj, 'was dropped on tile i', i, element)

					if (i === null) {
						console.log('Dropped on a non-tile')
						return false

					} else if ('player' in obj) {
						//
						// We're moving a player tile.
						//
						if (tile.classList.contains('cell-player')) {
							if (obj.player !== i) {
								console.log('Noticed player name swapping', obj.player, i)
								Haptics.impact({ style: ImpactStyle.Light })
								swapPlayers(obj.player, i)
								onSwapPlayers()
								return true
							}
						}
					} else {
						//
						// We're moving a position tile.
						//
						if (tile.classList.contains('cell-position')) {
							if (Number(i) === Number(obj.i)) {
								console.log('Dropped on self -ignoring')
								return false
							}
							if (Number(i) !== Number(obj.i)) {
								console.log('Invoking swapPositions!', i, obj)
								Haptics.impact({ style: ImpactStyle.Light })
								swapPositions(Number(obj.i), Number(i))
								return true
							}
						}
					}
				}
			} else if ('player' in obj && element.classList.contains('orangeman')) {

				//
				// We dropped something on the Orangeman space.
				//
				// console.log("Orangeman swapping")
				setOrangeman(obj.player)
				Haptics.impact({ style: ImpactStyle.Light })
				return true
			}

			// No Haptics if we don't change anything
			// console.log("Nothing to swap")
			return false
		})

		//
		// If we dropped a position back where it started, toggle its lock
		//
		if (!madeChanges && droppedOnPosition && !isQtrHeader && 'i' in obj && !obj.moved) {
			const index = Number(obj.i)
			if (!startLegacySubHelp(index)) {
				cancelLegacySubHelp()
				toggleLock(index)
			}
		}

		if (!madeChanges) {
			//
			// Force a render so that positions/players animate back to where we got them
			// from.
			//
			// If we're dragging a player, we don't want to execute this immediately because
			// that interrupts (and cancels) a click on their availability toggle.
			//
			if ('player' in obj) {
				setTimeout(forceUpdate, 100)
			} else {
				forceUpdate()
			}
		}
	}

	// console.log('display render', players, roster, data, players.length, roster.length, data.length)

	return (
		<>
		<div
			id="board"
			className={`grid grid-${numPeriods} grid-format-${gameFormat} grid-substitutions${substitutionDrag.dragState?.isTouch ? ' substitution-touch-drag-active' : ''}`}
		>
			{
				(showAltBoard && !showPositionPreferences) ? (
					<AltGridPositions />
				) : (
					<GridPlayers
						players={best.players}
						editMode={editMode}
						onDrop={onDrop}
						available={best.available}
						toggleAvailable={toggleAvailable}
						data={data}
						numPlayersShown={props.numPlayersShown}
						showAllPlayers={props.showAllPlayers}
						setShowAllPlayers={props.setShowAllPlayers}
						showPositionPreferences={showPositionPreferences}
						onDragStart={substitutionDrag.onNormalDragStart}
					/>
				)
			}
			{
				showPositionPreferences ?
					fieldPositions.map((position, index) => (
						<GridPreferences
							key={index}
							position={position}
							positionIndex={index}
							numPlayersShown={props.numPlayersShown}
						/>
					))
					:
					[ ...Array(numPeriods).keys() ].map(qtr => (
						<GridPositions
							key={qtr}
							best={best}
							data={data}
							onDrop={onDrop}
							qtr={qtr}
							showAltBoard={showAltBoard}
							endRoster={endRoster}
							substitutionDrag={substitutionDrag.dragState}
							recentSubstitutionIndexes={substitutionDrag.recentIndexes}
							pendingSubstitutionReturnIndex={substitutionDrag.pendingReturnIndex}
							gentleSubstitutionSettleIndex={substitutionDrag.gentleSettleIndex}
							legacySubHelpIndex={legacySubHelpIndex}
							onSubstitutionPointerDown={substitutionDrag.onPointerDown}
							onNormalDragStart={substitutionDrag.onNormalDragStart}
							numPlayersShown={props.numPlayersShown}
							onCourtPlayers={props.onCourtPlayers}
						/>
					))
			}
		</div>
		<SubstitutionDragOverlay
			dragState={substitutionDrag.dragState}
			dragPosition={substitutionDrag.dragPosition}
			dragGhostRef={substitutionDrag.dragGhostRef}
			returnGhost={substitutionDrag.returnGhost}
		/>
		<IonPopover
			ref={legacySubHelpPopover}
			isOpen={showLegacySubHelp}
			reference="event"
			className="substitution-gesture-help"
			onDidDismiss={() => setShowLegacySubHelp(false)}
		>
			<div className="substitution-gesture-help-content">
				<strong>Subs have a new gesture!</strong>
				<span>
                    Press and hold a position until it lifts up, then drag it.
                    <MoreInfoLink
						helpSection="tips"
						helpSubSection="subs"
						text=""
						onClick={() => setShowLegacySubHelp(false)}
					/>
                </span>
				<IonButton fill="clear" size="small" onClick={() => setShowLegacySubHelp(false)}>Okay then!</IonButton>
			</div>
		</IonPopover>
		</>
	)
}

const AltGridPositions = () => {

    const best = useMyBest()
	const gameFormat = useMyGameFormat()
	const fieldPositions = queryFieldPositions(gameFormat)

	const { available } = best

	if (!available)
		return null

	let numAvailablePlayers = Object.values(available).filter(isAvailable => isAvailable ? 1 : 0).length
	if (numAvailablePlayers < fieldPositions.length) {
		numAvailablePlayers = fieldPositions.length
	}

    const availablePositions = calculatePositions(numAvailablePlayers, gameFormat)

	return (
		<div className="column column-players">
			<GridCell
				type="void"
				content=""
			/>
			{
				availablePositions.map((position, index) => {
					const myClasses = [ "cell", "cell-position", `cell-position-${position}` ]
					return (
						<div key={index} className="fixed-element">
							<div className={myClasses.join(' ')}>
								{position}
							</div>
						</div>
					)
				})
			}
		</div>
	)
}

//
// e.g. getBoundingBox('[data-i="1"]')
//      => { xCenter: 123.45, yCenter: 123.45, x: 120, y: 100, width: ... }
//
function getBoundingBox(selector: string) {

	const ret: {
		xCenter: number | undefined,
		yCenter: number | undefined,
	} = {
		xCenter: undefined,
		yCenter: undefined,
	}

	//
	// Ionic can have hidden pages that are still in the DOM, so make sure we're
	// referencing the visible one.
	//
	// Note this is a bit fragile and might break if Ionic restructures how it
	// arranges its pages! I'm trying to make sure we don't select any
	// .ion-page-hidden content, but it's complicated by how there are multiple
	// .ion-page elements.
	//
	const fullSelector = `.ion-page:not(.ion-page-hidden) > ion-content #board ${selector}`
	const element = document.querySelector(fullSelector)

	if (element) {
		const box = element.getBoundingClientRect()

		ret.xCenter = (box.left + box.right) / 2
		ret.yCenter = (box.top + box.bottom) / 2
	}

	return ret
}

export default DisplayRoster
